import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const AMBER  = '#D97706'
const RED    = '#DC2626'

const FLAG_LEVEL_CONFIG = {
  watch:     { label: 'Watch',     color: AMBER,        bg: '#FFFBEB', border: '#FDE68A' },
  caution:   { label: 'Caution',   color: '#EA580C',    bg: '#FFF7ED', border: '#FED7AA' },
  high_risk: { label: 'High Risk', color: RED,          bg: '#FEF2F2', border: '#FECACA' },
}

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

function Pill({ label, color, bg }) {
  return <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>
}

function Card({ title, sub, children, noPad }) {
  return (
    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden', fontFamily: 'Space Grotesk, sans-serif', marginBottom: '16px' }}>
      {title && (
        <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{title}</div>
          {sub && <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{sub}</div>}
        </div>
      )}
      <div style={noPad ? {} : { padding: '16px 18px' }}>{children}</div>
    </div>
  )
}

export default function AdminFlags() {
  const [communityFlags, setCommunityFlags] = useState([])
  const [privateFlags, setPrivateFlags]     = useState([])
  // sellers map built locally in fetchAll — no state needed
  const [loading, setLoading]               = useState(true)
  const [search, setSearch]                 = useState('')
  const [levelFilter, setLevelFilter]       = useState('all')
  const [removingId, setRemovingId]         = useState(null)
  const [confirmRemove, setConfirmRemove]   = useState(null)
  const [activeTab, setActiveTab]           = useState('community')
  const [excessiveFlaggers, setExcessiveFlaggers] = useState([])

  useEffect(() => { fetchAll() }, [])

  const fetchAll = async () => {
    setLoading(true)
    const [cfRes, pfRes, sellersRes] = await Promise.all([
      supabase.from('community_flags').select('*').order('last_flagged_at', { ascending: false }),
      supabase.from('customer_flags').select('*, sellers:flagged_by_seller_id(shop_name)').eq('is_active', true).order('created_at', { ascending: false }),
      supabase.from('sellers').select('id, shop_name, email'),
    ])

    const allCF  = cfRes.data  || []
    const allPF  = pfRes.data  || []
    const allSel = sellersRes.data || []

    // Build seller map
    const selMap = {}
    allSel.forEach(s => { selMap[s.id] = s })


    setCommunityFlags(allCF)
    setPrivateFlags(allPF)

    // Find excessive flaggers — more than 10 flags this month
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
    const monthFlags = allPF.filter(f => f.created_at >= monthStart)
    const sellerFlagCount = {}
    monthFlags.forEach(f => { sellerFlagCount[f.flagged_by_seller_id] = (sellerFlagCount[f.flagged_by_seller_id] || 0) + 1 })
    const excessive = Object.entries(sellerFlagCount)
      .filter(([, count]) => count >= 10)
      .map(([sellerId, count]) => ({ sellerId, count, seller: selMap[sellerId] }))
      .sort((a, b) => b.count - a.count)
    setExcessiveFlaggers(excessive)

    setLoading(false)
  }

  const handleRemoveCommunityFlag = async (flag) => {
    setRemovingId(flag.id)
    await supabase.from('community_flags').delete().eq('id', flag.id)
    setCommunityFlags(prev => prev.filter(f => f.id !== flag.id))
    setConfirmRemove(null)
    setRemovingId(null)
  }

  const handleRemovePrivateFlag = async (flag) => {
    setRemovingId(flag.id)
    await supabase.from('customer_flags').update({ is_active: false, resolved_at: new Date().toISOString() }).eq('id', flag.id)
    setPrivateFlags(prev => prev.filter(f => f.id !== flag.id))
    setRemovingId(null)
  }

  const filteredCF = communityFlags.filter(f => {
    const matchSearch = !search || f.phone?.includes(search) || f.dominant_reason?.toLowerCase().includes(search.toLowerCase())
    const matchLevel  = levelFilter === 'all' || f.level === levelFilter
    return matchSearch && matchLevel
  })

  const stats = {
    total:     communityFlags.length,
    highRisk:  communityFlags.filter(f => f.level === 'high_risk').length,
    caution:   communityFlags.filter(f => f.level === 'caution').length,
    watch:     communityFlags.filter(f => f.level === 'watch').length,
    privateActive: privateFlags.length,
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Customer Flags</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>Community trust system — oversight & dispute management</div>
            </div>
            <button onClick={fetchAll} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer' }}>
              ↻ Refresh
            </button>
          </div>

          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '20px' }}>
            {[
              { label: 'Community flags',  value: stats.total,         color: BLUE,       bg: '#EFF6FF' },
              { label: 'High risk',        value: stats.highRisk,      color: RED,        bg: '#FEF2F2' },
              { label: 'Caution',          value: stats.caution,       color: '#EA580C',  bg: '#FFF7ED' },
              { label: 'Watch',            value: stats.watch,         color: AMBER,      bg: '#FFFBEB' },
              { label: 'Private flags',    value: stats.privateActive, color: MUTED,      bg: BG        },
            ].map(c => (
              <div key={c.label} style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, borderLeft: '3px solid ' + c.color, padding: '14px 16px' }}>
                <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '6px' }}>{c.label}</div>
                <div style={{ fontSize: '24px', fontWeight: 800, color: TEXT }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Excessive flaggers alert */}
          {excessiveFlaggers.length > 0 && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: AMBER, marginBottom: '8px' }}>
                ⚠ Sellers flagging excessively this month
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {excessiveFlaggers.map(e => (
                  <div key={e.sellerId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: WHITE, borderRadius: '8px', padding: '8px 12px' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: TEXT }}>{e.seller?.shop_name || e.sellerId}</span>
                      <span style={{ fontSize: '11.5px', color: MUTED, marginLeft: '8px' }}>{e.seller?.email}</span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: RED }}>{e.count} flags this month</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', marginBottom: '16px', width: 'fit-content' }}>
            {[
              { key: 'community', label: `Community Flags (${communityFlags.length})` },
              { key: 'private',   label: `All Private Flags (${privateFlags.length})` },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.key ? BLUE : 'transparent', color: activeTab === tab.key ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.12s' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '48px', textAlign: 'center', color: MUTED }}>
              Loading flag data...
            </div>
          ) : (
            <>
              {/* ── COMMUNITY FLAGS TAB ── */}
              {activeTab === 'community' && (
                <Card title="Community Flagged Customers" sub="Triggered when 3+ different sellers flag same phone number" noPad>
                  {/* Filters */}
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER, display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                      <input type="text" placeholder="Search phone or reason..." value={search} onChange={e => setSearch(e.target.value)}
                        style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 12px 8px 34px', fontSize: '12.5px', outline: 'none', background: BG, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box' }}
                        onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                      <div style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)' }}>
                        <Icon path="M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z" size={13} color={MUTED} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {['all','watch','caution','high_risk'].map(l => (
                        <button key={l} onClick={() => setLevelFilter(l)}
                          style={{ padding: '6px 12px', borderRadius: '20px', border: '1px solid', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', background: levelFilter === l ? (l === 'all' ? BLUE : FLAG_LEVEL_CONFIG[l]?.color || BLUE) : WHITE, color: levelFilter === l ? WHITE : MUTED, borderColor: levelFilter === l ? (l === 'all' ? BLUE : FLAG_LEVEL_CONFIG[l]?.color || BLUE) : BORDER }}>
                          {l === 'all' ? 'All' : FLAG_LEVEL_CONFIG[l]?.label}
                          <span style={{ marginLeft: '4px', opacity: 0.7 }}>{l === 'all' ? communityFlags.length : communityFlags.filter(f => f.level === l).length}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {filteredCF.length === 0 ? (
                    <div style={{ padding: '48px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>✓</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>{communityFlags.length === 0 ? 'No community flags yet' : 'No flags match your filter'}</div>
                      <div style={{ fontSize: '12px', color: MUTED }}>Community flags appear when 3+ sellers report the same phone number</div>
                    </div>
                  ) : filteredCF.map((flag, i) => {
                    const lc = FLAG_LEVEL_CONFIG[flag.level] || FLAG_LEVEL_CONFIG.watch
                    return (
                      <div key={flag.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', borderBottom: i < filteredCF.length - 1 ? '1px solid #F5F5F5' : 'none', background: flag.level === 'high_risk' ? '#FFF8F8' : WHITE }}>
                        <div style={{ fontSize: '11px', color: MUTED, width: '24px', textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{flag.phone}</span>
                            <Pill label={lc.label} color={lc.color} bg={lc.bg} />
                          </div>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: MUTED }}>
                              <strong style={{ color: TEXT }}>{flag.flag_count}</strong> seller{flag.flag_count !== 1 ? 's' : ''} reported
                            </span>
                            <span style={{ fontSize: '12px', color: MUTED }}>
                              Reason: <strong style={{ color: TEXT }}>{flag.dominant_reason?.replace(/_/g, ' ') || 'various'}</strong>
                            </span>
                            <span style={{ fontSize: '12px', color: MUTED }}>
                              ✓ {flag.successful_deliveries_since_last_flag || 0} clean deliveries since
                            </span>
                            <span style={{ fontSize: '12px', color: MUTED }}>
                              First: {new Date(flag.first_flagged_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </span>
                            <span style={{ fontSize: '12px', color: MUTED }}>
                              Last: {new Date(flag.last_flagged_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() => setConfirmRemove(flag)}
                          disabled={removingId === flag.id}
                          style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #FECACA', background: '#FEF2F2', fontSize: '12px', fontWeight: 600, color: RED, cursor: 'pointer', flexShrink: 0, opacity: removingId === flag.id ? 0.5 : 1 }}>
                          {removingId === flag.id ? 'Removing...' : 'Remove Flag'}
                        </button>
                      </div>
                    )
                  })}
                </Card>
              )}

              {/* ── PRIVATE FLAGS TAB ── */}
              {activeTab === 'private' && (
                <Card title="All Private Seller Flags" sub="Individual seller flags — only visible to the seller who raised them" noPad>
                  {privateFlags.length === 0 ? (
                    <div style={{ padding: '48px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>✓</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: TEXT }}>No active private flags</div>
                    </div>
                  ) : privateFlags.map((flag, i) => (
                    <div key={flag.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 18px', borderBottom: i < privateFlags.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                      <div style={{ fontSize: '11px', color: MUTED, width: '24px', textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{flag.phone}</span>
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '1px 7px', borderRadius: '20px', background: '#FEF2F2', color: RED }}>{flag.reason_category?.replace(/_/g, ' ')}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11.5px', color: MUTED }}>By: <strong style={{ color: TEXT }}>{flag.sellers?.shop_name || flag.flagged_by_seller_id}</strong></span>
                          {flag.reason_detail && <span style={{ fontSize: '11.5px', color: MUTED }}>Note: {flag.reason_detail}</span>}
                          <span style={{ fontSize: '11.5px', color: MUTED }}>{new Date(flag.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                        </div>
                      </div>
                      <button onClick={() => handleRemovePrivateFlag(flag)} disabled={removingId === flag.id}
                        style={{ padding: '5px 12px', borderRadius: '8px', border: '1px solid ' + BORDER, background: BG, fontSize: '11.5px', fontWeight: 600, color: MUTED, cursor: 'pointer', flexShrink: 0, opacity: removingId === flag.id ? 0.5 : 1 }}>
                        Remove
                      </button>
                    </div>
                  ))}
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm remove modal */}
      {confirmRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '28px', maxWidth: '400px', width: '100%', fontFamily: 'Space Grotesk, sans-serif', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚑</div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT, marginBottom: '8px' }}>Remove Community Flag?</div>
            <div style={{ fontSize: '12.5px', color: MUTED, marginBottom: '6px' }}>
              Phone: <strong style={{ color: TEXT, fontFamily: 'JetBrains Mono, monospace' }}>{confirmRemove.phone}</strong>
            </div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '20px', lineHeight: 1.6 }}>
              This will remove the community-level flag. Individual seller private flags are not affected. Only do this after reviewing a dispute.
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmRemove(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => handleRemoveCommunityFlag(confirmRemove)} disabled={removingId === confirmRemove.id}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: RED, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', opacity: removingId === confirmRemove.id ? 0.6 : 1 }}>
                {removingId === confirmRemove.id ? 'Removing...' : 'Remove Flag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}